import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { ROUTES } from "../consts/routes.consts";
import { isMetaAdsTokenExpired } from "../../utils/api.util";

interface ProtectedMetaAdsPublicRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component for Meta Ads public pages.
 * Checks if user has metaAdsPublicToken in localStorage.
 * If token exists, allows access to the children.
 * If token missing, redirects to meta-ads-login page.
 */
export const ProtectedMetaAdsPublicRoute: React.FC<ProtectedMetaAdsPublicRouteProps> = ({
  children,
}) => {
  const [isChecking, setIsChecking] = useState(true);
  const [hasToken, setHasToken] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // If token is expired, it is cleared by isMetaAdsTokenExpired()
    if (isMetaAdsTokenExpired()) {
      setIsChecking(false);
      return;
    }
    const token = localStorage.getItem("metaAdsPublicToken");
    if (token) {
      setHasToken(true);
    }
    setTimeout(() => {
      setIsChecking(false);
    }, 100);
  }, []);

  // Show loading while checking
  if (isChecking) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#0F0F11",
        }}
      >
        <CircularProgress style={{ color: "#58F2A9" }} />
      </div>
    );
  }

  // Redirect to login if no token
  if (!hasToken) {
    // Save the current location to redirect back after login
    return (
      <Navigate
        to={ROUTES.META_ADS_LOGIN}
        state={{ from: location.pathname }}
        replace={true}
      />
    );
  }

  // Allow access if token exists
  return <>{children}</>;
};

export default ProtectedMetaAdsPublicRoute;
